import { Fragment, lazy, Suspense } from 'react';
import MainLayout from '../layouts/MainLayout';
import BlankLayout from '../layouts/BlankLayout';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import { SUPPORTED_LANGUAGES } from '../i18nLanguages';
import { MobileAppProvider } from '../pages/mobile/MobileAppContext';

const Login = lazy(() => import('../pages/admin/login/Login'));
const Dashboard = lazy(() => import('../pages/home/Dashboard'));
const Users = lazy(() => import('../pages/admin/users/Users'));
const Settings = lazy(() => import('../pages/admin/settings/Settings'));
const Products = lazy(() => import('../pages/admin/products/Products'));
const CartRequests = lazy(() => import('../pages/admin/CartRequests/CartRequests'));
const CarWashes = lazy(() => import('../pages/admin/carWashes/CarWashes'));
const CarWashReport = lazy(() => import('../pages/admin/carWashes/CarWashReport'));
const AdminNotifications = lazy(
  () => import('../pages/admin/notifications/AdminNotifications')
);
const AdminStressTest = lazy(
  () => import('../pages/admin/stressTest/AdminStressTest')
);
const ForgotPassword = lazy(
  () => import('../pages/admin/forgotPassword/ForgotPassword')
);
const AcceptInvite = lazy(
  () => import('../pages/admin/acceptInvite/AcceptInvite')
);
const Setup2FaPage = lazy(
  () => import('../pages/admin/setup2faPage/Setup2FaPage')
);
const Verify2FaPage = lazy(
  () => import('../pages/admin/verify2faPage/Verify2FaPage')
);
const MobileLogin = lazy(() => import('../pages/mobile/login/MobileLogin'));
const MobileWelcome = lazy(() => import('../pages/mobile/welcome/MobileWelcome'));
const MobileShell = lazy(() => import('../pages/mobile/MobileShell'));
const MobileLocationPicker = lazy(
  () => import('../pages/mobile/locationPicker/MobileLocationPicker')
);
const PrivacyPolicy = lazy(() => import('../pages/privacyPolicy/PrivacyPolicy'));

const MobileProviderOutlet = () => (
  <MobileAppProvider>
    <Outlet />
  </MobileAppProvider>
);

const renderRouteSet = (prefix = '') => {
  return (
    <Fragment key={prefix || 'default'}>
      <Route element={<BlankLayout />}>
        <Route path={`${prefix}/login`} element={<Login />} />
        <Route path={`${prefix}/privacy-policy`} element={<PrivacyPolicy />} />
        <Route
          path={`${prefix}/forgot_password`}
          element={<ForgotPassword />}
        />
        <Route
          path={`${prefix}/invite/accept/:accessToken`}
          element={<AcceptInvite />}
        />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <MainLayout fullPage />
          </ProtectedRoute>
        }
      >
        <Route path={`${prefix}/dashboard`} element={<Dashboard />} />
        <Route path={`${prefix}/users`} element={<Users />} />
        <Route path={`${prefix}/products`} element={<Products />} />
        <Route path={`${prefix}/cart-requests`} element={<CartRequests />} />
        <Route path={`${prefix}/car-washes`} element={<CarWashes />} />
        <Route path={`${prefix}/car-washes/report/:id`} element={<CarWashReport />} />
        <Route path={`${prefix}/notifications`} element={<AdminNotifications />} />
        <Route path={`${prefix}/stress-test`} element={<AdminStressTest />} />
        <Route path={`${prefix}/settings`} element={<Settings />} />
      </Route>
      <Route
        path={`${prefix}/profile`}
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={<Navigate to="set_up_2_step_verification" replace />}
        />
        <Route path="set_up_2_step_verification" element={<Setup2FaPage />} />
        <Route
          path="turn_off_2_step_verification"
          element={<Verify2FaPage isDisabled />}
        />
      </Route>
      <Route
        path={`${prefix}/home`}
        element={<Navigate to={`${prefix}/dashboard`} replace />}
      />
      <Route
        path={`${prefix}/*`}
        element={<Navigate to={`${prefix}/login`} replace />}
      />
    </Fragment>
  );
};

const AppRoutes = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Standalone mobile attendance app (mock) */}
        <Route path="/mobile" element={<MobileProviderOutlet />}>
          <Route element={<BlankLayout centered={false} />}>
            <Route index element={<MobileWelcome />} />
            <Route path="home" element={<MobileShell />} />
            <Route
              path="location-picker/:kind"
              element={<MobileLocationPicker />}
            />
          </Route>
          <Route element={<BlankLayout centered />}>
            <Route path="login" element={<MobileLogin />} />
          </Route>
        </Route>

        {renderRouteSet()}
        {SUPPORTED_LANGUAGES.map((language) => renderRouteSet(`/${language}`))}
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
